import React, { useState } from 'react';
import { useAuth } from '@archicore/shared';

// Componente importado y adaptado de apps/control-asistencia
import { 
  ShieldCheck, Wrench, Database, AlertCircle, 
  RefreshCcw, ArrowRight, Save, LayoutGrid, Building2,
  ChevronLeft, Activity
} from 'lucide-react';
import { Link } from 'react-router-dom';

// Componente importado y adaptado de apps/control-asistencia
export const RepairDashboard: React.FC = () => {
    const { user } = useAuth();
    const [report, setReport] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [cleanupResult, setCleanupResult] = useState<any>(null);
    const [permissionsLog, setPermissionsLog] = useState<string[]>([]);
    const [error, setError] = useState<string | null>(null);

    // --- LÓGICA DE MIGRACIÓN DE DATOS ---
    const inspect = async () => {
        if (!user) return;
        setLoading(true);
        setError(null);
        try {
            const token = await user.getIdToken();
            const res = await fetch('/api/functions/inspectCommingledData', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            });
            if (!res.ok) throw new Error(await res.text());
            const data = await res.json();
            setReport(data.data);
        } catch (e: any) {
            setError(e.message || "Error al inspeccionar");
        } finally {
            setLoading(false);
        }
    };

    const cleanup = async () => {
        if (!user || !window.confirm("⚠️ ¿Mover datos raíz a 'estudio-lb'?")) return;
        setLoading(true);
        try {
            const token = await user.getIdToken();
            const res = await fetch('/api/functions/removeCommingledData?confirm=true', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            });
            if (!res.ok) throw new Error(await res.text());
            const data = await res.json();
            setCleanupResult(data.data);
            inspect();
        } catch (e: any) {
            setError(e.message || "Error al migrar");
        } finally {
            setLoading(false);
        }
    };

    // --- LÓGICA DE SINCRONIZACIÓN DE PERMISOS ---
    const addLog = (msg: string) => setPermissionsLog(prev => [...prev, `${new Date().toLocaleTimeString()} - ${msg}`]);

    const fixPermissions = async () => {
        if (!user) return;
        setLoading(true);
        setPermissionsLog([]);
        addLog("Iniciando reparación de permisos...");
        try {
            const token = await user.getIdToken();
            const res = await fetch('/api/functions/fixMyRole', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            });
            if (!res.ok) throw new Error(await res.text());
            const data = await res.json();
            addLog("✅ Éxito: " + (data.data?.message || "Permisos sincronizados."));
        } catch (e: any) {
            addLog(`❌ Error: ${e.message}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#050505] text-gray-100 p-8">
            <header className="max-w-7xl mx-auto mb-12 flex items-center justify-between">
                <div className="flex items-center gap-6">
                    <Link to="/" className="p-3 bg-gray-900 rounded-2xl border border-gray-800 hover:border-cyan-500/50 transition-all group">
                        <ChevronLeft className="text-gray-500 group-hover:text-cyan-400" size={24} />
                    </Link>
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <Wrench size={18} className="text-cyan-400" />
                            <span className="text-xs font-mono font-bold text-cyan-500 uppercase tracking-widest">Herramientas de Sistema</span>
                        </div>
                        <h1 className="text-4xl font-black text-white tracking-tight">Centro de Reparación</h1>
                    </div>
                </div>
                
                <div className="hidden md:flex items-center gap-3 px-4 py-2 bg-yellow-500/10 border border-yellow-500/20 rounded-xl text-yellow-500 text-xs font-bold uppercase tracking-tighter">
                    <AlertCircle size={14} />
                    Acceso Nivel 5: Super-Usuario
                </div>
            </header>

            <main className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* SECCIÓN 1: PERMISOS */}
                <div className="glass-card-accent p-8 rounded-3xl relative overflow-hidden flex flex-col h-full">
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-yellow-500 to-amber-600" />
                    
                    <div className="mb-8">
                        <div className="flex items-center justify-between mb-4">
                            <div className="p-3 bg-yellow-500/10 rounded-2xl border border-yellow-500/20">
                                <ShieldCheck size={32} className="text-yellow-500" />
                            </div>
                            <Activity className="text-yellow-500/20" size={64} />
                        </div>
                        <h2 className="text-2xl font-bold text-white mb-2">Sincronización de Identidad</h2>
                        <p className="text-gray-500 text-sm leading-relaxed">
                            Vuelve a sincronizar los "Custom Claims" de Firebase para tu usuario actual. Útil si has sido promovido a Super Admin recientemente.
                        </p>
                    </div>

                    <div className="flex-1 bg-black/40 border border-white/5 rounded-2xl p-4 font-mono text-xs overflow-y-auto mb-6 h-48 space-y-1">
                        {permissionsLog.length === 0 ? (
                            <span className="text-gray-700 italic">// Esperando orden de ejecución...</span>
                        ) : (
                            permissionsLog.map((l, i) => (
                                <div key={i} className={l.includes('❌') ? 'text-red-400' : 'text-emerald-400'}>{l}</div>
                            ))
                        )}
                    </div>

                    <button
                        onClick={fixPermissions}
                        disabled={loading}
                        className="w-full py-4 bg-yellow-600 hover:bg-yellow-500 text-white rounded-2xl font-black uppercase tracking-widest transition-all disabled:opacity-50 flex items-center justify-center gap-3 shadow-xl shadow-yellow-900/10"
                    >
                        {loading ? <RefreshCcw className="animate-spin" size={20} /> : <ShieldCheck size={20} />}
                        {loading ? 'Sincronizando...' : 'Sincronizar Permisos Ahora'}
                    </button>
                </div>

                {/* SECCIÓN 2: DATOS COMMINGLED */}
                <div className="glass-card p-8 rounded-3xl relative overflow-hidden flex flex-col h-full">
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-cyan-500" />
                    
                    <div className="mb-8">
                        <div className="flex items-center justify-between mb-4">
                            <div className="p-3 bg-blue-500/10 rounded-2xl border border-blue-500/20">
                                <Database size={32} className="text-blue-400" />
                            </div>
                            <RefreshCcw className="text-blue-500/20" size={64} />
                        </div>
                        <h2 className="text-2xl font-bold text-white mb-2">Unificación de Datos</h2>
                        <p className="text-gray-500 text-sm leading-relaxed">
                            Detecta y reubica datos "huérfanos" que quedaron en la raíz pública hacia el tenant 'estudio-lb'.
                        </p>
                    </div>

                    {error && (
                        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl mb-4 flex items-start gap-3">
                            <AlertCircle className="text-red-500 shrink-0" size={18} />
                            <p className="text-xs text-red-400">{error}</p>
                        </div>
                    )}

                    <div className="flex-1 space-y-4 mb-6">
                        <div className="flex gap-4">
                            <button 
                                onClick={inspect} 
                                disabled={loading} 
                                className="flex-1 py-3 bg-gray-900 border border-gray-800 text-gray-400 rounded-xl hover:bg-gray-800 hover:text-white transition-all text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-2"
                            >
                                <Building2 size={16} />
                                Inspeccionar Raíz
                            </button>
                            {report && (
                                <button 
                                    onClick={cleanup} 
                                    disabled={loading} 
                                    className="flex-1 py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-xl transition-all text-xs font-black uppercase tracking-wider flex items-center justify-center gap-2 shadow-lg shadow-blue-900/20"
                                >
                                    <ArrowRight size={16} />
                                    Migrar a lb
                                </button>
                            )}
                        </div>

                        {report && (
                            <div className="bg-black/40 border border-white/5 rounded-2xl p-4 font-mono text-[10px] text-blue-300 h-32 overflow-y-auto">
                                <pre>{JSON.stringify(report, null, 2)}</pre>
                            </div>
                        )}

                        {cleanupResult && (
                            <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl flex items-center gap-3">
                                <div className="p-1.5 bg-emerald-500/20 rounded-lg">
                                    <ShieldCheck className="text-emerald-400" size={16} />
                                </div>
                                <div>
                                    <p className="text-[10px] text-gray-500 uppercase font-bold tracking-widest">Resultado</p>
                                    <p className="text-xs text-emerald-400 font-bold">Migración completada con éxito</p>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="p-4 bg-gray-950/50 rounded-2xl border border-gray-800 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                            <Activity className="text-blue-500" size={20} />
                        </div>
                        <div className="flex-1">
                            <p className="text-[10px] text-gray-500 uppercase font-bold">Estado de DB</p>
                            <p className="text-xs text-white">Sincronización en curso...</p>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};
